import { useMutation } from '@apollo/client'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography
} from '@mui/material'
import React, { useState } from 'react'

import {
  CREATE_VOICEOVER_SHARE_TOKEN,
  CreateShareTokenData,
  CreateShareTokenVars
} from '../graphql'

type Props = {
  open: boolean
  onClose: () => void
  tourId: string
  modeId: string
  ext: string
}

const TTL_OPTIONS = [
  { value: 1, label: '1 Tag' },
  { value: 7, label: '7 Tage' },
  { value: 30, label: '30 Tage' }
]

export const ShareDialog: React.FC<Props> = ({
  open,
  onClose,
  tourId,
  modeId,
  ext
}) => {
  const [ttlDays, setTtlDays] = useState(7)
  const [link, setLink] = useState('')

  const [createToken, { loading, error }] = useMutation<
    CreateShareTokenData,
    CreateShareTokenVars
  >(CREATE_VOICEOVER_SHARE_TOKEN)

  const handleGenerate = async () => {
    const { data } = await createToken({
      variables: { tourId, modeId, ttlDays }
    })
    const token = data?.createVoiceoverShareToken
    if (token) {
      setLink(
        `${window.location.origin}/share/voiceover?token=${encodeURIComponent(token)}&ext=${ext}`
      )
    }
  }

  const handleClose = () => {
    setLink('')
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Voiceover-Seite teilen</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Erzeugt einen Link, über den die Voiceover-Seite ohne Login
          (read-only) geöffnet werden kann.
        </Typography>

        <TextField
          select
          fullWidth
          label="Gültigkeit"
          value={ttlDays}
          onChange={(e) => setTtlDays(Number(e.target.value))}
          disabled={loading}
        >
          {TTL_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        {link
          ? (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Link"
                value={link}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => navigator.clipboard?.writeText(link)}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>
            )
          : null}

        {error
          ? (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              Link konnte nicht erzeugt werden: {error.message}
            </Typography>
            )
          : null}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose}>Schließen</Button>
        <LoadingButton
          variant="contained"
          loading={loading}
          onClick={handleGenerate}
        >
          {link ? 'Neuen Link erzeugen' : 'Link erzeugen'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
